import { ScheduleColumnData } from '@data/schedules/types/ScheduleColumnData';
import BlockType from '@data/schedules/types/BlockType';
import { BlockReleaseState } from '@data/blockReleases/types/BlockReleaseState';
import { getDateFromApiDateTime } from '@calendar/utilities';
import {
	ParsedBlocks,
	UseBlockHeaderResult,
} from '@components/SchedulePage/types/BlockColumnHeaderDetailsType';
import SlotResponse from '@data/schedules/types/SlotResponse';
import { checkIsBlockReleased } from '@components/SchedulePage/utilities/checkIsBlockReleased/checkIsBlockReleased';
import {
	combineTimeFrames, extractBlockReleaseDetails
} from '@components/SchedulePage/utilities/blockTimeframeUtils/blockTimeframeUtils';

const getAdditionalBlockTimeframes = ({
	groupBlocks,
	specialtyBlocks,
}: {
	groupBlocks: Omit<SlotResponse, 'owners'>[];
	specialtyBlocks: Omit<SlotResponse, 'owners'>[];
}) => {
	return {
		'Group Block': groupBlocks.map((block) => ({
			start: getDateFromApiDateTime(block.start),
			end: getDateFromApiDateTime(block.end),
		})),
		'Specialty Block': specialtyBlocks.map((block) => ({
			start: getDateFromApiDateTime(block.start),
			end: getDateFromApiDateTime(block.end),
		})),
	};
};
export function getBlockColumnHeaderDetails(
	blockInfo: ScheduleColumnData,
): UseBlockHeaderResult {
	const defaultDetails = {
		isReleased: blockInfo?.isReleased,
		primaryType: BlockType.SURGEON,
		headerText: 'Surgeon Block',
		showUtilization: true,
		showTimeRange: true,
		showReleaseLinks: true,
		primaryTimeframes: [],
		hasAdditionalBlocks: false,
		showApproximateAvailableTime: true,
		additionalBlockTimeframes: {},
	};

	// Exclude GROUP and SPECIALTY blocks that were auto-released
	if (!blockInfo.blocks) return defaultDetails;

	const { surgeon, group, specialty } = blockInfo.blocks
		.filter((block) => {
			if (block?.release && block?.release?.state === BlockReleaseState.AUTO_RELEASE) {
				return !(block.type === BlockType.SPECIALTY || block.type === BlockType.GROUP);
			}
			return true;
		})
		.reduce<ParsedBlocks>(
			(acc, block) => {
				if (block.type === BlockType.SURGEON) {
					acc.surgeon.blocks.push(block);
					acc.surgeon.hasBlocks = true;
				} else if (block.type === BlockType.GROUP) {
					acc.group.blocks.push(block);
					acc.group.hasBlocks = true;
				} else if (block.type === BlockType.SPECIALTY) {
					acc.specialty.blocks.push(block);
					acc.specialty.hasBlocks = true;
				}
				return acc;
			},
			{
				surgeon: { blocks: [], hasBlocks: false },
				group: { blocks: [], hasBlocks: false },
				specialty: { blocks: [], hasBlocks: false },
			},
		);

	const { blocks: surgeonBlocks, hasBlocks: hasSurgeonBlock } = surgeon;
	const { blocks: groupBlocks, hasBlocks: hasGroupBlock } = group;
	const { blocks: specialtyBlocks, hasBlocks: hasSpecialtyBlock } = specialty;

	if (hasSurgeonBlock) {
		// Surgeon block takes priority over all others, displays utilization, approx time, and release links
		const nonReleasedSurgeonBlocks = surgeonBlocks.filter(block => !checkIsBlockReleased(block));
		const { surgeonTimeFrames } = extractBlockReleaseDetails(nonReleasedSurgeonBlocks);

		return {
			...defaultDetails,
			primaryTimeframes: combineTimeFrames(surgeonTimeFrames, nonReleasedSurgeonBlocks),
			additionalBlockTimeframes: getAdditionalBlockTimeframes({
				groupBlocks,
				specialtyBlocks,
			}),
			hasAdditionalBlocks: hasGroupBlock || hasSpecialtyBlock,
		};
	} else if (hasGroupBlock) {
		// Group block takes priority after surgeon block, but does not show utilization, approx time, or release links
		const { groupTimeFrames } = extractBlockReleaseDetails(groupBlocks);

		return {
			...defaultDetails,
			primaryType: BlockType.GROUP,
			headerText: 'Group Block',
			showUtilization: false,
			showReleaseLinks: false,
			primaryTimeframes: groupTimeFrames,
			additionalBlockTimeframes: getAdditionalBlockTimeframes({
				groupBlocks: [],
				specialtyBlocks,
			}),
			hasAdditionalBlocks: hasSpecialtyBlock,
			showApproximateAvailableTime: false,
		};
	} else if (hasSpecialtyBlock) {
		// Specialty block is primary only if alone, does not show utilization, approx time, or release links
		const { specialtyTimeFrames } = extractBlockReleaseDetails(specialtyBlocks);

		return {
			...defaultDetails,
			primaryType: BlockType.SPECIALTY,
			headerText: 'Specialty Block',
			showUtilization: false,
			showReleaseLinks: false,
			primaryTimeframes: specialtyTimeFrames,
			hasAdditionalBlocks: false,
			showApproximateAvailableTime: false,
			additionalBlockTimeframes: {},
		};
	}

	return defaultDetails;
}
